// communityPublicInformationStore.js
import { defineStore } from "pinia";
import axios from "axios";
import { BACKEND_BASEURL } from "@/config/config";

/**
 * Map raw API payload into a fully-typed CommunityPublicInformation domain object.
 *
 * @private
 * @param {Object} item – Raw object received from backend.
 * @returns {import('pinia').StateTree} Normalised community object with default fallbacks.
 */
export function _mapCommunityData(item) {
  return {
    ecn: item?.ecn || '',
    ecid: item?.ecid || '',
    name: item?.name || '',
    communityType: item?.communityType || '',
    dso: item?.dso || '',
    ecnDso: item?.ecnDso || '',
    legal: item?.legal || '',
    identificationType: item?.identificationType || '',
    identification: item?.identification || '',
    logoSrc: item?.logoSrc || '',
    uriMoreInfo: item?.uriMoreInfo || '',
    communityNotes: item?.communityNotes || '',
    allow0MeteringPoints: !!item?.allow0MeteringPoints,
    isDiscoverable: !!item?.isDiscoverable,
    needsSepaUpfront: !!item?.needsSepaUpfront,
    sepaAgreement: item?.sepaAgreement || { id: '', description: '', link: '' },
    // optional extended fields
    transformerReference: item?.transformerReference || '',
    substationReference: item?.substationReference || '',
    addressPostal: item?.addressPostal || { name: '', address: '', addressExtra: '', zip: '', city: '' },
    contacts: Array.isArray(item?.contacts) && item.contacts.length ? item.contacts : [],
    customAgreements: Array.isArray(item?.customAgreements) && item.customAgreements.length ? item.customAgreements : []
  };
}

/**
 * Community Public Information store
 * Uniquely identifies communities by ECN
 */
export const useCommunityPublicInformationStore = defineStore('communityPublicInformation', {
  state: () => ({
    communities: [],
  }),
  actions: {
    async fetchCommunities() {
      try {
        const response = await axios.get(`${BACKEND_BASEURL}communityPublicInformation`);
        
        if (!response.data || !Array.isArray(response.data)) {
          console.error('Invalid data format received from API');
          this.communities = [];
          return;
        }
        
        // Ensure all communities have the expected fields
        this.communities = response.data.map(_mapCommunityData);
      } catch (error) {
        console.error('Failed to fetch community information:', error);
        this.communities = [];
      }
    },

    async getCommunityByEcn(ecn) {
      try {
        const response = await axios.get(`${BACKEND_BASEURL}communityPublicInformation/${ecn}`);
        
        if (!response.data) {
          console.error('No data returned from API for ECN:', ecn);
          return null;
        }
        
        // Ensure all expected fields are present, even if null
        return _mapCommunityData(response.data);
      } catch (error) {
        console.error('Failed to fetch community information by ECN:', error);
        return null;
      }
    },

    async createCommunity(newCommunity) {
      try {
        const response = await axios.post(`${BACKEND_BASEURL}communityPublicInformation`, newCommunity);
        this.communities.push(response.data);
        return response.data;
      } catch (error) {
        console.error('Failed to create community information:', error);
        throw error;
      }
    },

    async updateCommunity(ecn, updatedInfo) {
      try {
        const response = await axios.put(`${BACKEND_BASEURL}communityPublicInformation/${ecn}`, updatedInfo);
        const index = this.communities.findIndex(community => community.ecn === ecn);
        if (index !== -1) {
          this.communities[index] = response.data;
        }
        return response.data;
      } catch (error) {
        console.error('Failed to update community information:', error);
        throw error;
      }
    },

    async deleteCommunity(ecn) {
      try {
        await axios.delete(`${BACKEND_BASEURL}communityPublicInformation/${ecn}`);
        this.communities = this.communities.filter(community => community.ecn !== ecn);
      } catch (error) {
        console.error('Failed to delete community information:', error);
        throw error;
      }
    }
  }
}); 